import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatDateTime } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowRight, Euro, Lock, Loader2, Truck, PartyPopper, Ban } from "lucide-react";

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800">{value || "—"}</span>
  </div>
);

export default function PublicCorsa() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/${token}`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F2F4F5] flex flex-col items-center px-4 py-10">
      <img src="/nexus_logo.png" alt="Nexus Italia Group" className="h-11 w-auto object-contain mb-6" />
      <div className="w-full max-w-md nx-fade-in">
        <div className="bg-primary text-white rounded-t-lg px-5 py-3">
          <p className="text-xs tracking-widest text-white/80">{data?.request_type === "evento" ? "NUOVO EVENTO" : "NUOVA RICHIESTA"}</p>
          <p className="font-semibold font-heading">{data?.request_type === "evento" ? "Evento / Manifestazione" : "Trasporto Sanitario"}</p>
        </div>
        <Card className="rounded-t-none border-slate-200 p-5">
          {err ? (
            <p className="text-center text-slate-500 py-10 text-sm">Link non valido o scaduto.</p>
          ) : !data ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : data.availability !== "disponibile" ? (
            <div data-testid="public-unavailable" className="text-center py-8">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#942F30]/10 flex items-center justify-center mb-4">
                <Ban className="h-7 w-7 text-[#942F30]" />
              </div>
              <span className="font-mono text-xs font-bold text-primary">{data.code}</span>
              <h2 data-testid="public-unavailable-title" className="text-lg font-semibold font-heading text-slate-900 mt-2">
                {data.availability === "assegnato" ? "Servizio già assegnato" : "Servizio non più disponibile"}
              </h2>
              <p data-testid="public-unavailable-message" className="text-sm text-slate-600 mt-2 max-w-xs mx-auto">
                {data.availability_message} Non è più possibile accettare o candidarsi per questo {data.request_type === "evento" ? "evento" : "trasporto"}.
              </p>
              <Button variant="outline" className="mt-6 gap-2" data-testid="public-unavailable-login" onClick={() => navigate("/login")}>
                Vai alle corse disponibili <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ) : data.request_type === "evento" ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs font-bold text-primary">{data.code}</span>
                <StatusBadge status={data.status} />
              </div>
              <p className="text-lg font-semibold text-slate-800 flex items-center gap-1.5 mb-4">
                <PartyPopper className="h-5 w-5 text-fuchsia-600" /> {data.evento?.denominazione || "Evento"}
              </p>
              <Row label="Tipo" value={data.evento?.tipo} />
              <Row label="Luogo" value={data.evento?.luogo} />
              <Row label="Inizio" value={formatDateTime(data.evento?.data_ora_inizio)} />
              <Row label="Fine" value={formatDateTime(data.evento?.data_ora_fine)} />
              <Row label="Risorse" value={(data.evento?.risorse_list || []).map((r) => `${r.label} x${r.qty}`).join(", ")} />
              <div className="flex justify-between py-3">
                <span className="text-slate-500 flex items-center gap-1 text-sm"><Euro className="h-4 w-4" /> Importo</span>
                <span className="font-bold text-emerald-700">€ {data.importo || "—"}</span>
              </div>
              <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-4">
                <Lock className="h-3 w-3" /> Accedi per accettare e sbloccare tutti i dettagli
              </p>
              <Button className="w-full gap-2" data-testid="public-login-cta" onClick={() => navigate("/login")}>
                <PartyPopper className="h-4 w-4" /> Accedi per Accettare
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs font-bold text-primary">{data.code}</span>
                <StatusBadge status={data.status} />
              </div>
              <p className="text-lg font-semibold text-slate-800 flex items-center gap-1.5 mb-4">
                {data.citta_partenza} <ArrowRight className="h-4 w-4 text-slate-400" /> {data.citta_arrivo}
              </p>
              <Row label="Data / Ora" value={formatDateTime(data.data_ora_evento)} />
              <Row label="Tipologia Mezzo" value={data.tipologia_mezzo} />
              <Row label="Mobilizzazione" value={data.mobilizzazione} />
              <Row label="Tratta" value={data.tratta} />
              <div className="flex justify-between py-3">
                <span className="text-slate-500 flex items-center gap-1 text-sm"><Euro className="h-4 w-4" /> Importo trasporto</span>
                <span className="font-bold text-emerald-700">€ {data.importo || "—"}</span>
              </div>
              <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-4">
                <Lock className="h-3 w-3" /> Accedi per accettare e sbloccare tutti i dettagli
              </p>
              <Button className="w-full gap-2" data-testid="public-login-cta" onClick={() => navigate("/login")}>
                <Truck className="h-4 w-4" /> Accedi per Accettare
              </Button>
            </>
          )}
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">Nexus Italia Group · Centrale Operativa 0287177344</p>
      </div>
    </div>
  );
}
